<template>
    <button type="button" class="button-container" :class="additionalClasses" :disabled="disabled" @click="onClick">
        <span class="name" :class="nameClasses">
            <template v-if="!loading">
                <slot>
                    {{ title }}
                </slot>
            </template>
            <Spinner v-else />
        </span>
    </button>
</template>
<script lang="ts">
    import { Component, Emit, Prop, Watch, Mixins } from 'vue-property-decorator';
    import { CoreThemeMixin } from '@/common/mixins';
    import Spinner from '@/common/components/spinner/Spinner.vue';

    /** @deprecated Use Button component from ui-kit instead */
    @Component({ components: { Spinner } })
    export default class CoreButton extends Mixins(CoreThemeMixin) {
        @Prop({ default: '' }) public title!: string;
        @Prop({ default: 'h4' }) public textClass!: string;
        @Prop({ default: false, type: Boolean }) public withBackground!: boolean;
        @Prop({ default: false, type: Boolean }) public centered!: boolean;
        @Prop({ default: false, type: Boolean }) public vCentered!: boolean;
        @Prop({ default: false, type: Boolean }) public outline!: boolean;
        @Prop({ default: false, type: Boolean }) public disabled!: boolean;
        @Prop({ default: 'large' }) public size;
        @Prop({ default: false, type: Boolean }) public loading!: boolean;
        @Prop({ default: true, type: Boolean }) public padding!: boolean;
        @Prop({ default: false, type: Boolean }) public isFullWidth!: boolean;

        public get additionalClasses(): string[] {
            return [
                this.withBackground ? 'has-background' : '',
                this.centered ? 'centered' : '',
                this.vCentered ? 'v-centered' : '',
                this.color,
                this.textClass,
                this.disabled ? 'disabled' : '',
                this.size,
                this.loading ? 'loading' : '',
                this.padding ? '' : 'no-padding',
                this.isFullWidth ? 'full-width-button' : '',
            ];
        }

        @Emit('click')
        public emitClick(e) {
            return e;
        }

        @Watch('loading')
        public setElementWidth(newValue, oldValue) {
            const element: HTMLElement = this.$el as HTMLElement;
            const innerElement: HTMLElement = element.querySelector('.name') as HTMLElement;
            if (innerElement) {
                innerElement.style.width = !oldValue && newValue ? `${element.offsetWidth}px` : '';
            }
        }

        public onClick(e) {
            if (this.disabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            this.emitClick(e);
        }

        public get nameClasses() {
            return {
                outline: this.outline,
                disabled: this.disabled,
            };
        }
    }
</script>

<style lang="less" scoped>
    @small-height: 35px;
    @large-height: 46px;

    .with-background-mixin(@mainColor, @textColor, @hoverColor, @borderWidth: 2px) {
        .name {
            overflow: hidden;
            color: @textColor;
            white-space: nowrap;
            text-align: center;
            text-overflow: ellipsis;
            background: @mainColor;
            border: @borderWidth solid @mainColor;

            &:hover:not(.disabled) {
                color: @textColor;
                background: @hoverColor;
                border: @borderWidth solid @hoverColor;
            }

            &.outline {
                color: @mainColor;
                white-space: nowrap;
                background: transparent;
                border: @borderWidth solid @mainColor;

                &:hover:not(.disabled) {
                    color: @bright-color;
                    background: @mainColor;
                    border: @borderWidth solid @mainColor;
                }
            }
        }
    }

    .no-background-mixin(@mainColor, @hoverColor) {
        .name {
            overflow: hidden;
            color: @mainColor;
            white-space: nowrap;
            text-overflow: ellipsis;

            &:hover:not(.disabled) {
                color: @hoverColor;
            }

            &.outline {
                color: @mainColor;
                white-space: nowrap;
                background: transparent;
                border: 2px solid @mainColor;

                &:hover:not(.disabled) {
                    color: @bright-color;
                    background: @mainColor;
                    border: 2px solid @mainColor;
                }
            }
        }
    }

    .button-container {
        display: flex;
        padding: 0;
        background-color: transparent;
        border: 0;
        user-select: none;

        &:focus {
            outline: none;
        }

        .name {
            .flex-center-content();
            .h4();

            height: @large-height;
            padding-right: @spacing-24;
            padding-left: @spacing-24;
            border: 0;
            border-radius: calc(@border-radius-10 * 4);
            cursor: pointer;

            &:hover,
            &-selected,
            &-active,
            &:focus {
                outline: 0;
            }
        }

        .spinner {
            margin: 0;

            ::v-deep div {
                background-color: currentColor !important;
            }
        }

        .name:not(.disabled) {
            transition: @generic-transition;

            &:hover,
            &-selected,
            &-active,
            &:focus {
                color: @bright-color;
            }
        }

        .small.name {
            .info-line-bold();

            height: @small-height;
            padding-right: @spacing-16;
            padding-left: @spacing-16;
        }

        &.centered {
            display: flex;
            justify-content: center;
        }

        &.v-centered {
            align-items: center;
        }

        &.disabled {
            @media (pointer: fine) {
                // stylelint-disable-next-line plugin/no-unsupported-browser-features
                cursor: not-allowed;
            }

            .name {
                opacity: 0.4;
                user-select: none;
                pointer-events: none;
            }
        }

        &.loading {
            pointer-events: none;
        }

        &.no-padding {
            .name {
                height: auto;
                padding: 0;
            }
        }
    }
    .full-width-button {
        .name {
            width: 100%;
        }
    }

    .button-container.has-background {
        &.success {
            .with-background-mixin(
                @mainColor: @success-color,
                @textColor: @bright-color,
                @hoverColor: @success-color-80
            );
        }

        &.info {
            .with-background-mixin(@mainColor: @info-color, @textColor: @bright-color, @hoverColor: @info-color-80);
        }

        &.info-20 {
            .with-background-mixin(@mainColor: @info-color-20, @textColor: @info-color, @hoverColor: @info-color-20, 0);
        }

        &.bright {
            .with-background-mixin(@mainColor: @bright-color, @textColor: @bright-color, @hoverColor: @info-color-80);
        }

        &.warning {
            .with-background-mixin(
                @mainColor: @warning-color,
                @textColor: @bright-color,
                @hoverColor: @warning-color-80
            );
        }

        &.danger {
            .with-background-mixin(@mainColor: @danger-color, @textColor: @bright-color, @hoverColor: @danger-color-80);
        }

        &.primary {
            .with-background-mixin(
                @mainColor: @primary-color,
                @textColor: @bright-color,
                @hoverColor: @hover-primary-color
            );
        }

        &.branding {
            .with-background-mixin(
                @mainColor: @branding-color,
                @textColor: @bright-color,
                @hoverColor: @hover-branding-color
            );
        }

        &.dark {
            .with-background-mixin(@mainColor: @dark-color, @textColor: @bright-color, @hoverColor: @dark-color-80);
        }

        .with-background-mixin(
            @mainColor: @primary-color,
            @textColor: @bright-color,
            @hoverColor: @hover-primary-color
        );
    }

    .button-container:not(.has-background) {
        &.success {
            .no-background-mixin(@mainColor: @success-color, @hoverColor: @success-color-80);
        }

        &.info {
            .no-background-mixin(@mainColor: @info-color, @hoverColor: @info-color-80);
        }

        &.bright {
            .no-background-mixin(@mainColor: @bright-color, @hoverColor: @bright-color-80);
        }

        &.warning {
            .no-background-mixin(@mainColor: @warning-color, @hoverColor: @warning-color-80);
        }

        &.danger {
            .no-background-mixin(@mainColor: @danger-color, @hoverColor: @danger-color-80);
        }

        &.primary {
            .no-background-mixin(@mainColor: @primary-color, @hoverColor: @hover-primary-color);
        }

        &.branding {
            .no-background-mixin(@mainColor: @branding-color, @hoverColor: @hover-branding-color);
        }

        &.dark {
            .no-background-mixin(@mainColor: @dark-color, @hoverColor: @dark-color-80);
        }
    }
</style>
